import React, { Fragment } from "react";
import { StoryCard } from "../../Molecules/StoryCard";
import { StorycardContent } from "../../Molecules/StorycardContent";
import { HeroImage } from "../../Atoms/HeroImage";
import get from "lodash/get";
import { CollectionName } from "../../Atoms/CollectionName";
import { LoadmoreButton } from "../../Atoms/Loadmore";
import { generateNavigateSlug, getTextColor, navigateTo } from "../../../utils/utils";
import { collectionToStories } from "@quintype/components";
import PropTypes from "prop-types";
import { StateProvider } from "../../SharedContext";
import "./four-col.m.css";
import { ProgressiveHydration } from "../../../hydration-component";
import { useDispatch, useSelector } from "react-redux";
import { LoadMoreTarget } from "../../Atoms/LoadMoreTarget";
import ListingAdSlot from "../../../../components/molecules/ListingAdSlot";
import { getCategoryAndSubCategory, getTemplateName } from "../../../../ga-utils";

const loadMore = ({ isLoading, storyItems, getMoreStories, subsequentLoadCount = 4 }) => {
  if (isLoading) return; // prevent multiple clicks/calls
  const limit = subsequentLoadCount;
  const offset = storyItems.length;
  getMoreStories(offset, limit);
};

const FourColGrid = ({
  collection,
  config = {},
  getMoreStories,
  isLoadMoreVisible,
  isLoading,
  isolatedLoadMore,
  showCollectionName,
  showMultipleAuthor,
  showAuthorContent,
  sectionSlug,
  pageType,
  l2onl1page = false,
}) => {
  const {
    collectionNameBorderColor = "",
    borderColor = "",
    theme = "",
    border = "",
    collectionNameTemplate = "",
    footerSlotConfig = {},
    footerButton = "",
    customCollectionName,
    navigate = true,
    subsequentLoadCount = 4,
  } = config;
  const storyItems = collectionToStories(collection);

  if (!storyItems.length) return null;

  const { footerSlot } = footerSlotConfig;
  const footerSlotComp = footerSlot ? footerSlot() : null;
  const textColor = getTextColor(theme);

  const dispatch = useDispatch();
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));

  let templateName;
  templateName = getTemplateName(pageType);
  const trackEvent = (linkPosition) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: linkPosition,
        section_name: collection?.name ? collection.name?.toLowerCase() : "NA",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const handleStoryClick = (index) => {
    const position = index + 1;
    trackEvent(position);
  };
  const getLoadMore = (opts) => {
    if (!isLoadMoreVisible) return null;

    if (isolatedLoadMore) {
      return (
        <LoadMoreTarget
          collection={collection}
          componentName={"FourColGrid"}
          offset={storyItems.length}
          limit={subsequentLoadCount}
        />
      );
    }

    const url = generateNavigateSlug(collection, qtConfig);
    return (
      <ProgressiveHydration>
        <LoadmoreButton
          config={config}
          collection={collection}
          template={footerButton}
          onClick={() => loadMore(opts)}
          navigate={() => navigateTo(dispatch, url)}
          qtConfig={qtConfig}
        />
      </ProgressiveHydration>
    );
  };

  return (
    <div className="full-width-with-padding arrow-component" data-test-id="four-col-grid">
      <div className="four-col-grid" styleName="four-col-grid">
        {showCollectionName && (
          <CollectionName
            collection={collection}
            collectionNameTemplate={collectionNameTemplate}
            collectionNameBorderColor={collectionNameBorderColor}
            customCollectionName={customCollectionName}
            navigate={navigate}
          />
        )}
        <div className="four-col-grid" styleName="wrapper">
          {storyItems.map((story, index) => {
            return (
              <Fragment key={`story-4col-${index}-${get(story, ["id"], "0")}`}>
                <div
                  className="story-card-wrapper"
                  styleName={`story-card-wrapper ${l2onl1page ? "l2onl1-wrapper" : ""}`}
                  key={`default-${index}`}
                  onClick={() => handleStoryClick(index)}
                >
                  <StoryCard story={story} border={border} theme={theme} isHorizontalMobile config={config}>
                    <HeroImage aspectRatio={[[16, 9]]} story={story} isHorizontalMobile />
                    <StorycardContent
                      theme={theme}
                      border={border}
                      borderColor={borderColor}
                      story={story}
                      isHorizontalMobile
                      config={config}
                      showMultipleAuthor={showMultipleAuthor}
                      showAuthorContent={showAuthorContent}
                      sectionSlug={sectionSlug}
                    />
                  </StoryCard>
                </div>
                {pageType === "magazines-issue-landing-page" && index === 4 && (
                  <ListingAdSlot
                    adStyleName="ad-slot-size-300x250"
                    adType="grid_ad"
                    pageType="magazines-issue-landing-page"
                    adSlotConfigName="mRec_ad"
                  />
                )}
              </Fragment>
            );
          })}
        </div>
        {getLoadMore({ isLoading, storyItems, getMoreStories, subsequentLoadCount })}
        {footerSlotComp}
      </div>
    </div>
  );
};

FourColGrid.propTypes = {
  /**  collection is the array of objects which is returning by API  */
  collection: PropTypes.object.isRequired,
  /** The config for the Row */
  config: PropTypes.shape({
    borderColor: PropTypes.string,
    theme: PropTypes.string,
    border: PropTypes.bool,
    slotConfig: PropTypes.array,
    collectionNameTemplate: PropTypes.string,
    footerButton: PropTypes.string,
    collectionNameBorderColor: PropTypes.string,
    subsequentLoadCount: PropTypes.number,
  }),
  getMoreStories: PropTypes.func,
  isLoadMoreVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  isolatedLoadMore: PropTypes.bool,
  showCollectionName: PropTypes.bool,
  showMultipleAuthor: PropTypes.bool,
  l2onl1page: PropTypes.bool,
};

FourColGrid.defaultProps = {
  getMoreStories: () => {},
  isLoadMoreVisible: true,
  isLoading: false,
  showCollectionName: true,
  showMultipleAuthor: true,
};

export default StateProvider(FourColGrid);
